import { requestVisionCompletion } from "utils/aiClient.ts";

export type ExpiryExtractionStatus = "success" | "ambiguous" | "unreadable";

export interface ExpiryExtractionResult {
  status: ExpiryExtractionStatus;
  isoDate: string | null;
  rawText?: string;
  confidence?: number;
  reason?: string;
}

interface ExpiryAIResponse {
  status?: unknown;
  year?: unknown;
  month?: unknown;
  day?: unknown;
  rawText?: unknown;
  confidence?: unknown;
  reason?: unknown;
}

export function fileToDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Failed to read image"));
    };
    reader.onerror = () => reject(reader.error ?? new Error("Failed to read image"));
    reader.readAsDataURL(file);
  });
}

export function normalizeExpiryExtractionResponse(value: unknown, today: Date = new Date()): ExpiryExtractionResult {
  const parsed = parseExpiryAIResponse(value);
  const status = parsed.status;
  const rawText = typeof parsed.rawText === "string" ? parsed.rawText.trim() : undefined;
  const reason = typeof parsed.reason === "string" ? parsed.reason.trim() : undefined;
  const confidence = normalizeConfidence(parsed.confidence);

  if (status !== "success") {
    return {
      status,
      isoDate: null,
      rawText,
      confidence,
      reason: reason || defaultReasonForStatus(status),
    };
  }

  const year = normalizeYear(parsed.year);
  const month = normalizeInteger(parsed.month);
  const day = normalizeOptionalDay(parsed.day);

  if (!month) {
    return {
      status: "ambiguous",
      isoDate: null,
      rawText,
      confidence,
      reason: reason || "The date was not specific enough to use.",
    };
  }

  const resolvedYear = year ?? inferNearestFutureYear(month, day, today);
  if (!resolvedYear) {
    return {
      status: "ambiguous",
      isoDate: null,
      rawText,
      confidence,
      reason: reason || "The date was not specific enough to use.",
    };
  }

  const isoDate = buildIsoDate(resolvedYear, month, day, today);
  if (!isoDate || isoDate === "2999-12-31") {
    return {
      status: "ambiguous",
      isoDate: null,
      rawText,
      confidence,
      reason: reason || "The detected date was invalid.",
    };
  }

  return {
    status: "success",
    isoDate,
    rawText,
    confidence,
    reason,
  };
}

export async function extractExpiryDateFromImage(file: Blob): Promise<ExpiryExtractionResult> {
  const imageDataUrl = await fileToDataUrl(file);
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const prompt = [
    "Read the food package expiry information from this image.",
    "Only identify a printed expiry, best before, or use by date that is actually visible.",
    "If a visible date is present and it could plausibly be the expiry date, treat it as the expiry date even when the label text is unclear or partially obscured.",
    `Today's date is ${todayIso}.`,
    "Return JSON only with this shape:",
    '{"status":"success|ambiguous|unreadable","year":2026,"month":3,"day":31,"rawText":"31/03/2026","confidence":0.98,"reason":"short explanation"}',
    "Rules:",
    "- Use status=success only when a plausible expiry date is visible.",
    "- If you can see a date but you are not fully sure it is the expiry date, assume it is the expiry date unless the image clearly shows it is some other kind of date.",
    "- If the label shows only month and year, set day to null.",
    "- If the label shows day and month but no year, use the nearest future occurrence relative to today's date.",
    `- Example: if today's date is 2026-02-15 and the label says 20 FEB, return year=2026, month=2, day=20.`,
    "- Ignore short numeric suffixes, batch codes, or trailing fragments that are not plausible 4-digit years. For example, if the label says 20 FEB 054, treat 054 as noise and use the day/month rule instead of year=54.",
    "- Dates more than 10 years in the future are invalid and should not be returned as success.",
    "- If the image is blurry, unreadable, or no expiry text is visible, use status=unreadable.",
    "- If multiple conflicting dates are visible, or the image clearly suggests the visible date is not an expiry-related date, use status=ambiguous.",
    "- Return numbers for year, month, and day. Do not include markdown or extra text.",
    "- Do not include markdown or extra text.",
  ].join("\n");

  const responseText = await requestVisionCompletion({
    systemPrompt: "You extract expiry dates from grocery package photos. Prefer treating a visible plausible date as the expiry date unless the image clearly indicates otherwise. Respond with strict JSON only.",
    prompt,
    imageDataUrl,
  });

  let parsed: unknown;
  try {
    parsed = JSON.parse(stripCodeFences(responseText));
  } catch (error) {
    throw new Error(`Expiry extraction returned invalid JSON: ${error instanceof Error ? error.message : String(error)}`);
  }

  return normalizeExpiryExtractionResponse(parsed, today);
}

function parseExpiryAIResponse(value: unknown): ExpiryAIResponse & { status: ExpiryExtractionStatus } {
  const obj = value && typeof value === "object" ? (value as ExpiryAIResponse) : {};
  const status = obj.status === "success" || obj.status === "ambiguous" || obj.status === "unreadable"
    ? obj.status
    : "ambiguous";
  return { ...obj, status };
}

function normalizeInteger(value: unknown): number | null {
  if (typeof value === "number" && Number.isInteger(value)) return value;
  if (typeof value === "string" && /^\d+$/.test(value.trim())) return Number(value.trim());
  return null;
}

function normalizeYear(value: unknown): number | null {
  const parsed = normalizeInteger(value);
  if (parsed === null) return null;
  return parsed >= 2000 && parsed <= 2998 ? parsed : null;
}

function normalizeOptionalDay(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  return normalizeInteger(value);
}

function normalizeConfidence(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  if (value < 0 || value > 1) return undefined;
  return value;
}

function defaultReasonForStatus(status: Exclude<ExpiryExtractionStatus, "success">): string {
  return status === "unreadable"
    ? "No readable expiry date was found in the image."
    : "The image did not contain one reliable expiry date.";
}

function inferNearestFutureYear(month: number, day: number | null, today: Date): number | null {
  if (day === null) return null;

  const todayUtc = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  for (let year = todayUtc.getUTCFullYear(); year <= todayUtc.getUTCFullYear() + 8; year++) {
    const iso = buildIsoDate(year, month, day, todayUtc);
    if (!iso) continue;
    const candidate = new Date(`${iso}T00:00:00Z`);
    if (candidate >= todayUtc) {
      return year;
    }
  }

  return null;
}

function buildIsoDate(year: number, month: number, day: number | null, today: Date = new Date()): string | null {
  if (year < 2000 || year > 2998) return null;
  if (month < 1 || month > 12) return null;

  const resolvedDay = day ?? getDaysInMonth(year, month);
  if (resolvedDay < 1 || resolvedDay > getDaysInMonth(year, month)) return null;

  const iso = `${year.toString().padStart(4, "0")}-${month.toString().padStart(2, "0")}-${resolvedDay.toString().padStart(2, "0")}`;
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  if (date.toISOString().slice(0, 10) !== iso) return null;

  const todayUtc = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const maxFutureDate = new Date(Date.UTC(todayUtc.getUTCFullYear() + 10, todayUtc.getUTCMonth(), todayUtc.getUTCDate()));
  if (date > maxFutureDate) return null;

  return iso;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function stripCodeFences(text: string): string {
  return text.replace(/^\s*```[a-zA-Z]*\s*\r?\n/, "").replace(/\r?\n?\s*```\s*$/, "").trim();
}
